import { useRef, useState } from 'react'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

type AppSelectOption = {
  value: string
  label: string
}

type AppSelectProps = {
  id?: string
  ariaLabel?: string
  value: string
  onValueChange(value: string): void
  options: AppSelectOption[]
  disabled?: boolean
  size?: 'sm' | 'default'
  variant?: 'outline' | 'link'
  triggerLabel?: string
  triggerClassName?: string
  focusAfterSelectionId?: string
  onArrowNavigate?(direction: -1 | 1): void
}

const emptySelectValue = '__c_sheet_empty__'

export function AppSelect({
  id,
  ariaLabel,
  value,
  onValueChange,
  options,
  disabled,
  size,
  variant,
  triggerLabel,
  triggerClassName,
  focusAfterSelectionId,
  onArrowNavigate
}: AppSelectProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const selectionCommitted = useRef(false)
  const arrowDirection = useRef<-1 | 1 | null>(null)
  const selectOptions = options.map((option) => ({
    ...option,
    value: option.value || emptySelectValue
  }))

  return (
    <Select
      open={open}
      items={selectOptions}
      value={value || emptySelectValue}
      onValueChange={(nextValue) =>
        onValueChange(nextValue === emptySelectValue || nextValue === null ? '' : nextValue)
      }
      disabled={disabled}
      onOpenChange={(open, details) => {
        setOpen(open)
        if (open) arrowDirection.current = null
        selectionCommitted.current = !open && details.reason === 'item-press'
      }}
    >
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        size={size}
        variant={variant}
        className={cn('w-full', triggerClassName)}
      >
        {triggerLabel === undefined ? (
          <SelectValue />
        ) : (
          <span data-slot="select-value" className="min-w-0 flex-1 truncate text-left">
            {triggerLabel}
          </span>
        )}
      </SelectTrigger>
      <SelectContent
        alignItemWithTrigger={false}
        onKeyDown={(event) => {
          if (
            !onArrowNavigate ||
            (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') ||
            event.defaultPrevented ||
            event.nativeEvent.isComposing ||
            event.shiftKey ||
            event.ctrlKey ||
            event.altKey ||
            event.metaKey
          ) {
            return
          }
          event.preventDefault()
          event.stopPropagation()
          arrowDirection.current = event.key === 'ArrowLeft' ? -1 : 1
          selectionCommitted.current = false
          setOpen(false)
        }}
        finalFocus={() => {
          if (arrowDirection.current !== null && onArrowNavigate) {
            const direction = arrowDirection.current
            arrowDirection.current = null
            onArrowNavigate(direction)
            return false
          }
          if (!selectionCommitted.current || !focusAfterSelectionId) return true
          const nextField = document.getElementById(focusAfterSelectionId)
          return nextField && !nextField.matches(':disabled') ? nextField : true
        }}
      >
        <SelectGroup>
          {selectOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
